/**
 * Device analytics — iOS app device model breakdowns for CRM Analytics page.
 */

import { buildSegmentBreakdown } from "./growthAnalyticsMetrics";
import {
  segmentDeviceOs,
  segmentDeviceModel,
  hasDeviceModelReported,
  isIphoneProContact,
  isIpadProContact,
  formatDeviceModelLabel,
} from "./deviceProfile";
import { tableRowsToCSV } from "./productAnalyticsMetrics";

export const DEVICE_SEGMENT_EXPORT_KEYS = new Set([
  "iphone_pro",
  "ipad_pro",
  "non_iphone_pro",
  "non_ipad_pro",
]);

function deviceUpdatedMs(contact) {
  const v = contact?.deviceUpdatedAt;
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function signupPool(contacts = [], { includeInternal = false } = {}) {
  if (includeInternal) return contacts.slice();
  return contacts.filter((c) => !c.isInternal);
}

function contactsForSegment(segmentKey, contacts = [], opts = {}) {
  const pool = signupPool(contacts, opts);
  switch (segmentKey) {
    case "with_model":
      return pool.filter(hasDeviceModelReported);
    case "iphone_pro":
      return pool.filter(isIphoneProContact);
    case "ipad_pro":
      return pool.filter(isIpadProContact);
    case "non_iphone_pro":
      return pool.filter((c) => !isIphoneProContact(c));
    case "non_ipad_pro":
      return pool.filter((c) => !isIpadProContact(c));
    default:
      return pool.filter(hasDeviceModelReported);
  }
}

export function computeDeviceAnalytics(contacts = [], { includeInternal = false } = {}) {
  const pool = signupPool(contacts, { includeInternal });
  const internalPool = contacts.filter((c) => c.isInternal);
  const internalWithModel = internalPool.filter(hasDeviceModelReported).length;
  const totalSignUps = pool.length;
  const withModel = pool.filter(hasDeviceModelReported);
  const withModelCount = withModel.length;

  const iphonePro = pool.filter(isIphoneProContact).length;
  const ipadPro = pool.filter(isIpadProContact).length;
  const nonIphonePro = totalSignUps - iphonePro;
  const nonIpadPro = totalSignUps - ipadPro;

  const pctOfSignUps = (n) => (totalSignUps ? (n / totalSignUps) * 100 : null);
  const lastDeviceSyncMs = withModel.reduce((max, c) => Math.max(max, deviceUpdatedMs(c)), 0);

  return {
    totalSignUps,
    withDeviceModel: withModelCount,
    coveragePct: pctOfSignUps(withModelCount),
    lastDeviceSyncMs,
    internalWithModel,
    internalIphonePro: internalPool.filter(isIphoneProContact).length,
    includeInternal,
    iphonePro,
    ipadPro,
    nonIphonePro,
    nonIpadPro,
    iphoneProPct: pctOfSignUps(iphonePro),
    ipadProPct: pctOfSignUps(ipadPro),
    nonIphoneProPct: pctOfSignUps(nonIphonePro),
    nonIpadProPct: pctOfSignUps(nonIpadPro),
    segments: {
      model: buildSegmentBreakdown(withModel, segmentDeviceModel, { maxRows: 20 }),
      os: buildSegmentBreakdown(withModel, segmentDeviceOs, { maxRows: 12 }),
    },
    computedAt: Date.now(),
  };
}

function deviceDrillRows(contacts, segmentKey, opts = {}) {
  return contactsForSegment(segmentKey, contacts, opts)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((c) => ({
      k: c.id,
      cells: [
        c.name || "—",
        c.email || "—",
        formatDeviceModelLabel(c.deviceModel) || "—",
        c.deviceOs || "—",
      ],
      contact: c,
    }));
}

const SEGMENT_TITLES = {
  with_model: "Sign-ups with device model",
  iphone_pro: "iPhone Pro",
  ipad_pro: "iPad Pro",
  non_iphone_pro: "Non–iPhone Pro",
  non_ipad_pro: "Non–iPad Pro",
  model: "Device model",
  os: "Operating system",
};

export function deviceDrillContent(deviceKey, _deviceAnalytics, contacts = [], filterLabel = "", opts = {}) {
  const segmentKey = filterLabel || deviceKey;

  if (deviceKey === "model" || deviceKey === "os") {
    const withModel = signupPool(contacts, opts).filter(hasDeviceModelReported);
    const labelFn = deviceKey === "os" ? segmentDeviceOs : segmentDeviceModel;
    const rows = withModel
      .filter((c) => !filterLabel || labelFn(c) === filterLabel)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((c) => ({
        k: c.id,
        cells: [
          c.name || "—",
          c.email || "—",
          formatDeviceModelLabel(c.deviceModel) || "—",
          c.deviceOs || "—",
        ],
        contact: c,
      }));

    return {
      subtitle: filterLabel
        ? `${rows.length} sign-up(s) · ${SEGMENT_TITLES[deviceKey]}: ${filterLabel}`
        : `${rows.length} sign-up(s) with a reported device model`,
      headers: ["Name", "Email", "Device", "OS"],
      rows,
      showContactActions: true,
      csvExportable: false,
    };
  }

  const rows = deviceDrillRows(contacts, segmentKey, opts);
  const title = SEGMENT_TITLES[segmentKey] || segmentKey;

  return {
    subtitle: `${rows.length} sign-up(s) · ${title}`,
    headers: ["Name", "Email", "Device", "OS"],
    rows,
    showContactActions: true,
    csvExportable: DEVICE_SEGMENT_EXPORT_KEYS.has(segmentKey),
  };
}

export function deviceSegmentCsvFilename(segmentKey) {
  const slug =
    {
      iphone_pro: "iphone-pro-signups",
      ipad_pro: "ipad-pro-signups",
      non_iphone_pro: "non-iphone-pro-signups",
      non_ipad_pro: "non-ipad-pro-signups",
      with_model: "device-model-coverage",
    }[segmentKey] || String(segmentKey).replace(/_/g, "-");
  return `scanbrix-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
}

export function downloadDeviceSegmentCsv(segmentKey, contacts = [], opts = {}) {
  const table = deviceDrillContent(segmentKey, null, contacts, "", opts);
  if (!table?.headers?.length) return;
  const csv = tableRowsToCSV(table.headers, table.rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = deviceSegmentCsvFilename(segmentKey);
  a.click();
  URL.revokeObjectURL(url);
}
